/**
 * Selector Derivation
 *
 * Derives stable selectors from Playwright accessibility snapshots.
 * These selectors can be used to locate elements during cache replay
 * even though the ephemeral refs have changed.
 *
 * Selector priority (tried in order during replay):
 * 1. data-testid (most stable when available)
 * 2. Role + name (a11y-focused apps)
 * 3. Label text (getByLabel)
 * 4. Placeholder text
 * 5. Text content
 * 6. CSS selector (fallback, least stable)
 *
 * @module workflows/cache/selector-derivation
 * @see docs/cache-replay.md
 */

import type { Page, Locator } from 'playwright';
import type {
  SelectorStrategy,
  ElementInfo,
  ParentContext,
  ParentContextDynamicText,
  FrameContext,
} from '@chatsdet/types';
import { createLogger } from './logger.js';
import { resolveElement, type LocatorContext } from './element-resolver.js';

// Lazy logger to avoid env validation at module load time (for tests)
let _logger: ReturnType<typeof createLogger> | null = null;
const getLogger = () => {
  if (!_logger) {
    _logger = createLogger({ module: 'SelectorDerivation' });
  }
  return _logger;
};

// ─────────────────────────────────────────────────────────────────────────────
// Selector Verification Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of verifying a single selector type
 */
interface SelectorVerificationResult {
  /** The selector type that was verified */
  selectorType: 'testId' | 'role' | 'ariaLabel' | 'placeholder' | 'text';
  /** Whether the selector was successfully verified */
  verified: boolean;
  /** Number of elements that matched the selector */
  matchCount: number;
  /** Whether the resolved element coordinates match the original */
  coordinatesMatch: boolean;
  /** Original element center coordinates */
  originalCoords?: { x: number; y: number };
  /** Resolved element center coordinates */
  resolvedCoords?: { x: number; y: number };
  /** Reason for rejection (if not verified) */
  reason?: string;
  /** Parent context if disambiguation was needed */
  parentContext?: ParentContext;
}

/**
 * Result of verifying all selectors for an element
 */
interface SelectorVerificationSummary {
  /** Selectors that passed verification (only verified selectors included) */
  verifiedSelectors: SelectorStrategy;
  /** Individual verification results for each selector type */
  results: SelectorVerificationResult[];
  /** Whether any selector was successfully verified */
  anyVerified: boolean;
}

/**
 * Options for selector verification
 */
interface SelectorVerificationOptions {
  /** @deprecated No longer used — DOM identity comparison replaced coordinates */
  tolerancePx?: number;
  /** Max depth for parent scope search (default: 5) */
  maxParentDepth?: number;
  /** Timeout in ms for locator operations (default: 5000) */
  timeoutMs?: number;
  /**
   * For typeable/form controls, skip weak text selector probing once a stronger
   * selector is available. This prevents form labels/icons from becoming
   * expensive getByText() candidates during recording.
   */
  skipTextWhenStrong?: boolean;
}

type SelectorType = 'testId' | 'role' | 'ariaLabel' | 'placeholder' | 'text';

/**
 * Roles eligible for ariaLabel fallback from accessibleName.
 * These are interactive/input roles that commonly use aria-labelledby for
 * labeling and where getByRole name matching can be unreliable.
 */
const LABEL_FALLBACK_ROLES = new Set([
  'combobox',
  'textbox',
  'searchbox',
  'listbox',
  'spinbutton',
  'slider',
  'switch',
  'checkbox',
  'radio',
  'menuitemcheckbox',
  'menuitemradio',
  'generic',
  'none',
  'presentation',
]);

const TYPEABLE_SELECTOR_ROLES = new Set(['textbox', 'searchbox', 'spinbutton', 'combobox', 'input']);

const stripPrivateUseGlyphs = (value: string): string => value.replace(/[\uE000-\uF8FF]/g, '');

const normalizeText = (value: string): string =>
  stripPrivateUseGlyphs(value).replace(/\s+/g, ' ').trim();

const escapeCssAttributeValue = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const buildFormParentCss = (opts: { name?: string | null; id?: string | null }): string => {
  if (opts.name && opts.name.trim().length > 0) {
    return `form[name="${escapeCssAttributeValue(opts.name.trim())}"]`;
  }
  if (opts.id && opts.id.trim().length > 0) {
    return `form[id="${escapeCssAttributeValue(opts.id.trim())}"]`;
  }
  return 'form';
};

const stripNoiseSuffixes = (value: string): string =>
  value
    .replace(/\s+Not bookmarked$/i, '')
    .replace(/\s+Bookmarked$/i, '')
    .replace(/\s+\u2026$/i, '')
    .replace(/\s+…$/i, '')
    .trim();

const buildContainsText = (elementInfo: ElementInfo): string[] | undefined => {
  const raw = elementInfo.textContent || elementInfo.accessibleName;
  if (!raw) return undefined;
  const normalized = stripNoiseSuffixes(normalizeText(raw));
  if (!normalized || normalized.length < 3) return undefined;
  return [normalized];
};

/**
 * Derive stable selectors from an element's info.
 *
 * @param elementInfo - Element info extracted from snapshot
 * @returns Selector strategy with multiple fallback selectors
 */
export function deriveSelectorsFromElementInfo(elementInfo: ElementInfo): SelectorStrategy {
  const selectors: SelectorStrategy = {};

  // 0. data-testid selector (most stable when available)
  if (elementInfo.dataTestId) {
    selectors.testId = elementInfo.dataTestId;
  }

  const roleName = elementInfo.accessibleName || elementInfo.labelText || elementInfo.ariaLabel;

  // 1. Role-based selector (most stable)
  if (
    elementInfo.role &&
    elementInfo.role !== 'generic' &&
    elementInfo.role !== 'text' &&
    elementInfo.role !== 'none' &&
    elementInfo.role !== 'presentation'
  ) {
    selectors.role = {
      role: elementInfo.role,
      name: roleName,
      // Use non-exact matching for resilience to whitespace differences
      exact: roleName ? false : undefined,
    };
  }

  const containsText = buildContainsText(elementInfo);
  if (
    containsText &&
    elementInfo.role &&
    ['row', 'listitem', 'option', 'menuitem', 'treeitem', 'gridcell', 'article'].includes(
      elementInfo.role
    )
  ) {
    selectors.containsText = containsText;
  }

  // 2. Label selector (covers aria-label, <label> text, and aria-labelledby)
  if (elementInfo.labelText || elementInfo.ariaLabel) {
    selectors.ariaLabel = elementInfo.labelText || elementInfo.ariaLabel;
  } else if (
    elementInfo.accessibleName &&
    elementInfo.accessibleName.length >= 3 &&
    elementInfo.role &&
    LABEL_FALLBACK_ROLES.has(elementInfo.role)
  ) {
    // For interactive/input roles that commonly use aria-labelledby for labeling,
    // fall back to the computed accessible name. getByLabel() uses DOM label
    // relationships which is a genuinely different matching mechanism than
    // getByRole(), providing resilience when one fails (e.g. Dynamics 365
    // comboboxes using aria-labelledby).
    selectors.ariaLabel = elementInfo.accessibleName;
  }

  // 3. Placeholder selector (inputs)
  if (elementInfo.placeholder) {
    selectors.placeholder = elementInfo.placeholder;
  }

  // 4. Text content selector (visible text)
  // Apply same 3-char minimum as buildContainsText to avoid short-string false positives
  if (elementInfo.textContent) {
    const normalizedForLength = normalizeText(elementInfo.textContent);
    if (normalizedForLength.length >= 3) {
      selectors.text = {
        text: elementInfo.textContent,
        exact: false, // Allow partial match for robustness
      };
    }
  }

  // 5. Parent context for disambiguation
  if (elementInfo.parentRole || elementInfo.parentName) {
    const parentCss = elementInfo.parentRole === 'form' ? 'form' : undefined;
    selectors.parentContext = {
      role: elementInfo.parentRole,
      name: elementInfo.parentName,
      css: parentCss,
      nameFromHeading: elementInfo.parentNameFromHeading || undefined,
    };
  }

  // 6. Frame context for elements inside iframes
  if (elementInfo.frameContext) {
    selectors.frameContext = elementInfo.frameContext;
  }

  // 7. Data attributes for list/table disambiguation
  if (elementInfo.itemIndex !== undefined) {
    selectors.itemIndex = elementInfo.itemIndex;
  }
  if (elementInfo.ariaRowIndex !== undefined) {
    selectors.rowIndex = elementInfo.ariaRowIndex;
  }
  if (elementInfo.automationId) {
    selectors.automationId = elementInfo.automationId;
  }

  // 8. Child filter for disambiguation (e.g., child img alt text)
  if (elementInfo.childImgAlt) {
    selectors.childFilter = { role: 'img', name: elementInfo.childImgAlt };
  }

  return selectors;
}

export { buildLocatorString } from '@chatsdet/recording';


/**
 * Sanitize tool arguments for caching.
 *
 * Removes ephemeral refs and other non-stable data.
 *
 * @param args - Original tool arguments
 * @returns Sanitized arguments safe for caching
 */
export function sanitizeArgsForCache(args: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(args)) {
    // Skip ephemeral refs - these will be resolved via selectors during replay
    if (key === 'ref' || key === 'startRef' || key === 'endRef') {
      continue;
    }

    // Skip signal and timeout options - these are runtime-specific
    if (key === 'signal' || key === 'timeoutMs') {
      continue;
    }

    // Deep clone arrays (like fields in fillForm)
    if (Array.isArray(value)) {
      sanitized[key] = value.map((item) => {
        if (typeof item === 'object' && item !== null) {
          return sanitizeArgsForCache(item as Record<string, unknown>);
        }
        return item;
      });
      continue;
    }

    // Keep other values
    sanitized[key] = value;
  }

  return sanitized;
}

// ─────────────────────────────────────────────────────────────────────────────
// Selector Verification
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default configuration for selector verification
 */
const DEFAULT_VERIFICATION_OPTIONS: Required<SelectorVerificationOptions> = {
  tolerancePx: 10,
  maxParentDepth: 5,
  timeoutMs: 5000,
  skipTextWhenStrong: false,
};

/**
 * Semantic roles that make good scoping parents for disambiguation
 */
const SEMANTIC_PARENT_ROLES = [
  'dialog',
  'form',
  'region',
  'navigation',
  'main',
  'article',
  'section',
  'complementary',
  'banner',
  'contentinfo',
  'alertdialog',
  'menu',
  'menubar',
  'tablist',
  'tabpanel',
  'toolbar',
  'treegrid',
  'grid',
  'listbox',
];

/**
 * Compare two Playwright locators by DOM identity.
 *
 * Uses page.evaluate to check `a === b` in the browser.
 * Returns false on any failure (timeout, detached element, etc.).
 */
async function isSameDOMElement(
  page: Page,
  locatorA: Locator,
  locatorB: Locator,
  timeoutMs: number
): Promise<boolean> {
  let handleA: Awaited<ReturnType<Locator['elementHandle']>> | null = null;
  let handleB: Awaited<ReturnType<Locator['elementHandle']>> | null = null;
  try {
    [handleA, handleB] = await Promise.race([
      Promise.all([locatorA.elementHandle(), locatorB.elementHandle()]),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('elementHandle timeout')), timeoutMs)
      ),
    ]);
    if (!handleA || !handleB) return false;
    const same = await page.evaluate(([a, b]) => a === b, [handleA, handleB] as const);
    return same;
  } catch {
    return false;
  } finally {
    await handleA?.dispose().catch(() => {});
    await handleB?.dispose().catch(() => {});
  }
}

/**
 * Build a minimal SelectorStrategy containing only the specified selector type.
 * Preserves data attributes, frame context, and existing parent context for
 * disambiguation, so the shared resolver exercises the exact same code path as replay.
 */
function buildMinimalStrategy(
  selectors: SelectorStrategy,
  selectorType: SelectorType,
  parentContextOverride?: ParentContext
): SelectorStrategy | null {
  const strategy: SelectorStrategy = {};

  switch (selectorType) {
    case 'testId':
      if (!selectors.testId) return null;
      strategy.testId = selectors.testId;
      break;
    case 'role':
      if (!selectors.role) return null;
      strategy.role = { ...selectors.role };
      if (selectors.containsText) strategy.containsText = [...selectors.containsText];
      break;
    case 'ariaLabel':
      if (!selectors.ariaLabel) return null;
      strategy.ariaLabel = selectors.ariaLabel;
      break;
    case 'placeholder':
      if (!selectors.placeholder) return null;
      strategy.placeholder = selectors.placeholder;
      break;
    case 'text':
      if (!selectors.text) return null;
      strategy.text = { ...selectors.text };
      break;
  }

  // Preserve data attributes for disambiguation
  if (selectors.itemIndex !== undefined) strategy.itemIndex = selectors.itemIndex;
  if (selectors.rowIndex !== undefined) strategy.rowIndex = selectors.rowIndex;
  if (selectors.automationId) strategy.automationId = selectors.automationId;
  if (selectors.frameContext) strategy.frameContext = selectors.frameContext;
  if (selectors.childFilter) strategy.childFilter = selectors.childFilter;

  // Use override or existing parent context
  if (parentContextOverride) {
    strategy.parentContext = parentContextOverride;
  } else if (selectors.parentContext) {
    strategy.parentContext = selectors.parentContext;
  }

  return strategy;
}

const hasStrongFormSelector = (selectors: SelectorStrategy): boolean =>
  Boolean(
    selectors.testId ||
      (selectors.role?.role &&
        TYPEABLE_SELECTOR_ROLES.has(selectors.role.role) &&
        selectors.role.name) ||
      selectors.ariaLabel ||
      selectors.placeholder
  );

const shouldSkipTextVerification = (
  selectors: SelectorStrategy,
  options: Pick<SelectorVerificationOptions, 'skipTextWhenStrong'>
): boolean =>
  Boolean(
    options.skipTextWhenStrong &&
      selectors.text &&
      selectors.role?.role &&
      TYPEABLE_SELECTOR_ROLES.has(selectors.role.role) &&
      hasStrongFormSelector(selectors)
  );

export function getSelectorTypesForVerification(
  selectors: SelectorStrategy,
  options: Pick<SelectorVerificationOptions, 'skipTextWhenStrong'> = {}
): SelectorType[] {
  const selectorTypes: SelectorType[] = ['testId', 'role', 'ariaLabel', 'placeholder'];
  if (!shouldSkipTextVerification(selectors, options)) {
    selectorTypes.push('text');
  }
  return selectorTypes;
}

/**
 * Walk up from the target element to the nearest row-like ancestor and
 * propose candidate text tokens that would uniquely identify that row.
 *
 * Returns the row's ARIA role plus a ranked list of text candidates to try
 * as `.filter({ hasText: … })` anchors. Callers should verify each candidate
 * against the actual selector before committing it.
 */
async function findRowAnchor(
  originalLocator: Locator,
  timeoutMs: number
): Promise<{ role: string; candidates: string[] } | null> {
  try {
    const result = await Promise.race([
      originalLocator.evaluate((el) => {
        const ROW_ROLES = new Set([
          'row',
          'listitem',
          'gridcell',
          'cell',
          'treeitem',
          'option',
        ]);
        const implicitRoleFor = (tag: string): string | null => {
          switch (tag) {
            case 'tr':
              return 'row';
            case 'li':
              return 'listitem';
            case 'td':
              return 'cell';
            case 'th':
              return 'cell';
            default:
              return null;
          }
        };

        // Walk up to find the nearest row-like ancestor.
        let current: Element | null = el.parentElement;
        let rowEl: Element | null = null;
        let rowRole: string | null = null;
        while (current) {
          const roleAttr = current.getAttribute('role');
          const tag = current.tagName.toLowerCase();
          const role = roleAttr || implicitRoleFor(tag);
          if (role && ROW_ROLES.has(role)) {
            rowEl = current;
            rowRole = role;
            break;
          }
          current = current.parentElement;
        }
        if (!rowEl || !rowRole) return null;

        const targetText = (el.textContent || '').trim().toLowerCase();
        const ariaLabel = rowEl.getAttribute('aria-label')?.trim();

        // Collect candidate text tokens from row descendants, excluding the
        // target element's own subtree.
        const texts: string[] = [];
        const seen = new Set<string>();
        const pushText = (raw: string | null | undefined) => {
          if (!raw) return;
          const t = raw.trim();
          if (t.length < 3) return;
          if (t.length > 120) return;
          const key = t.toLowerCase();
          if (key === targetText) return;
          if (targetText.length > 0 && key.includes(targetText)) return;
          if (targetText.length > 0 && targetText.includes(key)) return;
          if (seen.has(key)) return;
          seen.add(key);
          texts.push(t);
        };

        if (ariaLabel) pushText(ariaLabel);

        // Prefer cell-level descendants first; they tend to carry the
        // distinguishing value (e.g. a variable name).
        const cellSelectors = [
          '[role="gridcell"]',
          '[role="cell"]',
          '[role="rowheader"]',
          'th',
          'td',
        ];
        for (const sel of cellSelectors) {
          for (const cell of Array.from(rowEl.querySelectorAll(sel))) {
            if (el.contains(cell) || cell.contains(el)) continue;
            pushText(cell.textContent);
            if (texts.length >= 4) break;
          }
          if (texts.length >= 4) break;
        }

        // Fall back to any visible text nodes not inside the target.
        if (texts.length === 0) {
          const walker = document.createTreeWalker(rowEl, NodeFilter.SHOW_TEXT, {
            acceptNode(node) {
              if (!node.textContent || !node.textContent.trim()) {
                return NodeFilter.FILTER_REJECT;
              }
              let ancestor: Node | null = node;
              while (ancestor) {
                if (ancestor === el) return NodeFilter.FILTER_REJECT;
                ancestor = ancestor.parentNode;
              }
              return NodeFilter.FILTER_ACCEPT;
            },
          });
          let node = walker.nextNode();
          while (node && texts.length < 4) {
            pushText(node.textContent);
            node = walker.nextNode();
          }
        }

        return { role: rowRole, candidates: texts };
      }),
      new Promise<null>((_, reject) =>
        setTimeout(() => reject(new Error('findRowAnchor timeout')), timeoutMs)
      ),
    ]);

    if (!result) return null;
    const normalizedCandidates = result.candidates
      .map((raw) => stripNoiseSuffixes(normalizeText(raw)))
      .filter((t): t is string => !!t && t.length >= 3)
      // Prefer shorter, distinctive tokens over long blobs.
      .sort((a, b) => a.length - b.length)
      .slice(0, 4);
    if (normalizedCandidates.length === 0) return null;
    return { role: result.role, candidates: normalizedCandidates };
  } catch {
    return null;
  }
}

/**
 * Try to discover a parent context that disambiguates the selector.
 *
 * Walks DOM ancestry of all matching elements to find a semantic parent scope
 * that makes the selector unique. Uses the shared resolver to verify each
 * candidate, ensuring the same code path as replay.
 *
 * @param page - Playwright page
 * @param selectors - The selector strategy being verified
 * @param selectorType - Which selector type to scope
 * @param originalLocator - Locator for the original element (via aria-ref)
 * @param options - Verification options
 * @returns Parent context, or null
 */
async function discoverParentContext(
  page: Page,
  selectors: SelectorStrategy,
  selectorType: 'testId' | 'role' | 'ariaLabel' | 'placeholder' | 'text',
  originalLocator: Locator,
  options: Required<SelectorVerificationOptions>
): Promise<{
  parentContext: ParentContext;
} | null> {
  const { timeoutMs, maxParentDepth } = options;
  const logger = getLogger();

  // Determine the correct context: use frame locator for iframe-scoped selectors
  const context: LocatorContext = selectors.frameContext?.selector
    ? page.frameLocator(selectors.frameContext.selector)
    : page;

  // Strategy 0: Row-anchor scoping. When the target sits inside a row-like
  // ancestor (role=row/listitem/gridcell/treeitem/option), prefer anchoring
  // to that row using a unique text token inside it. This is almost always
  // more stable than the nearest landmark (main/form/region), which is what
  // Strategy 1 would otherwise pick.
  try {
    const rowCandidate = await findRowAnchor(originalLocator, timeoutMs);
    if (rowCandidate && rowCandidate.candidates.length > 0) {
      for (const candidateText of rowCandidate.candidates) {
        const candidateParent: ParentContext = {
          role: rowCandidate.role,
          containsText: candidateText,
        };
        const strategyWithParent = buildMinimalStrategy(selectors, selectorType, candidateParent);
        if (!strategyWithParent) continue;
        try {
          const resolved = await resolveElement(page, strategyWithParent, timeoutMs, {
            mode: 'verify',
          });
          if (!resolved) continue;
          const isSame = await isSameDOMElement(page, originalLocator, resolved, timeoutMs);
          if (!isSame) continue;
          logger.debug('[SelectorDerivation] Found unique row anchor', {
            selectorType,
            parentContext: candidateParent,
          });
          return { parentContext: candidateParent };
        } catch {
          continue;
        }
      }
    }
  } catch (err) {
    logger.debug('[SelectorDerivation] Row-anchor strategy error', {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  // Strategy 1: Simple parent scoping - try each semantic parent role
  try {
    for (const parentRole of SEMANTIC_PARENT_ROLES) {
      const parentLocators = context.getByRole(parentRole as Parameters<Page['getByRole']>[0]);

      let parentCount: number;
      try {
        parentCount = await Promise.race([
          parentLocators.count(),
          new Promise<number>((_, reject) =>
            setTimeout(() => reject(new Error('Count timeout')), timeoutMs)
          ),
        ]);
      } catch {
        continue;
      }

      for (let i = 0; i < parentCount; i++) {
        const parentLocator = parentLocators.nth(i);

        try {
          // Check if original element is a DOM descendant of this parent
          let origHandle: Awaited<ReturnType<Locator['elementHandle']>> | null = null;
          let parentHandle: Awaited<ReturnType<Locator['elementHandle']>> | null = null;
          let isWithinParent = false;
          try {
            [parentHandle, origHandle] = await Promise.race([
              Promise.all([parentLocator.elementHandle(), originalLocator.elementHandle()]),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('elementHandle timeout')), timeoutMs)
              ),
            ]);
            if (parentHandle && origHandle) {
              isWithinParent = await page.evaluate(([p, c]) => p.contains(c), [
                parentHandle,
                origHandle,
              ] as const);
            }
          } catch {
            isWithinParent = false;
          } finally {
            await parentHandle?.dispose().catch(() => {});
            await origHandle?.dispose().catch(() => {});
          }

          if (!isWithinParent) continue;

          // Get parent's accessible name
          let parentName: string | undefined;
          let nameFromHeading: boolean | undefined;
          try {
            parentName = (await parentLocator.getAttribute('aria-label')) ?? undefined;
            if (!parentName) {
              const innerText = await parentLocator.evaluate((el) => {
                const legend = el.querySelector('legend');
                if (legend) return { text: legend.textContent?.trim(), fromHeading: false };
                const heading = el.querySelector('h1, h2, h3, h4, h5, h6');
                if (heading) return { text: heading.textContent?.trim(), fromHeading: true };
                return undefined;
              });
              if (innerText?.text) {
                parentName = innerText.text;
                nameFromHeading = innerText.fromHeading || undefined;
              }
            }
          } catch {
            // Ignore
          }

          // Build strategy with this parent context and try resolveElement
          let parentCss: string | undefined;
          if (parentRole === 'form') {
            const formMeta = await parentLocator
              .evaluate((el) => {
                if (el.tagName.toLowerCase() !== 'form') return null;
                return {
                  name: el.getAttribute('name'),
                  id: el.getAttribute('id'),
                };
              })
              .catch(() => null);
            parentCss = buildFormParentCss({
              name: formMeta?.name,
              id: formMeta?.id,
            });
          }

          const candidateParent: ParentContext = {
            role: parentRole,
            name: parentName,
            ...(parentCss ? { css: parentCss } : {}),
            nameFromHeading,
          };
          const strategyWithParent = buildMinimalStrategy(selectors, selectorType, candidateParent);
          if (!strategyWithParent) continue;

          // If resolveElement returns a locator, verify it's the SAME element as the original target.
          // This prevents accepting a parent scope where the selector resolves but to the wrong element.
          const resolved = await resolveElement(page, strategyWithParent, timeoutMs, {
            mode: 'verify',
          });
          if (resolved) {
            const isSame = await isSameDOMElement(page, originalLocator, resolved, timeoutMs);
            if (!isSame) {
              logger.debug(
                '[SelectorDerivation] Parent-scoped selector resolved to wrong element, skipping',
                {
                  selectorType,
                  parentContext: candidateParent,
                }
              );
              continue;
            }
            logger.debug('[SelectorDerivation] Found unique parent via simple scoping', {
              selectorType,
              parentContext: candidateParent,
            });
            return { parentContext: candidateParent };
          }
        } catch {
          continue;
        }
      }
    }
  } catch (err) {
    logger.debug('[SelectorDerivation] Error in simple parent scoping', {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  // Strategy 2: Recursive parent chain from DOM ancestry
  // Build a basic locator for counting and ancestry extraction
  const minimalStrategy = buildMinimalStrategy(selectors, selectorType);
  if (!minimalStrategy) return null;

  // We need a raw locator for DOM introspection (ancestry extraction).
  // Build one in the correct context (frame or page) for this purpose.
  let rawLocator: Locator | null = null;
  switch (selectorType) {
    case 'testId':
      if (selectors.testId) rawLocator = context.getByTestId(selectors.testId);
      break;
    case 'role':
      if (selectors.role) {
        const { role, name, exact } = selectors.role;
        rawLocator = context.getByRole(
          role as Parameters<Page['getByRole']>[0],
          name ? { name, exact: exact ?? false } : undefined
        );
      }
      break;
    case 'ariaLabel':
      if (selectors.ariaLabel) rawLocator = context.getByLabel(selectors.ariaLabel);
      break;
    case 'placeholder':
      if (selectors.placeholder) rawLocator = context.getByPlaceholder(selectors.placeholder);
      break;
    case 'text':
      if (selectors.text)
        rawLocator = context.getByText(selectors.text.text, {
          exact: selectors.text.exact ?? false,
        });
      break;
  }

  if (!rawLocator) return null;

  let count: number;
  try {
    count = await Promise.race([
      rawLocator.count(),
      new Promise<number>((_, reject) =>
        setTimeout(() => reject(new Error('Count timeout')), timeoutMs)
      ),
    ]);
  } catch {
    return null;
  }

  if (count <= 1) return null;

  // Identify which match is the target using DOM identity comparison,
  // and build ancestry for each matching element
  const elementAncestries: Array<{
    index: number;
    isTarget: boolean;
    ancestors: Array<{ role: string; name?: string; css?: string; nameFromHeading?: boolean }>;
  }> = [];

  for (let i = 0; i < Math.min(count, 10); i++) {
    const nth = rawLocator.nth(i);
    try {
      // Use DOM identity to identify the target element (replaces coordinate comparison)
      const isTarget = await isSameDOMElement(page, originalLocator, nth, timeoutMs);

      const ancestry = await nth.evaluate((el) => {
        const SEMANTIC_ROLES = [
          'dialog',
          'form',
          'region',
          'navigation',
          'main',
          'article',
          'section',
          'complementary',
          'listbox',
          'grid',
          'tree',
          'menu',
          'tabpanel',
          'toolbar',
          'treegrid',
          'banner',
          'contentinfo',
          'alertdialog',
          'menubar',
          'tablist',
        ];
        const parents: Array<{
          role: string | null;
          name: string | null;
          css: string | null;
          nameFromHeading: boolean;
        }> = [];
        let current = el.parentElement;
        while (current && parents.length < 10) {
          const roleAttr = current.getAttribute('role');
          const tagName = current.tagName.toLowerCase();
          const implicitRole =
            tagName === 'nav'
              ? 'navigation'
              : tagName === 'main'
                ? 'main'
                : tagName === 'form'
                  ? 'form'
                  : tagName === 'dialog'
                    ? 'dialog'
                    : tagName === 'article'
                      ? 'article'
                      : tagName === 'section'
                        ? 'region'
                        : tagName === 'aside'
                          ? 'complementary'
                          : tagName === 'header'
                            ? 'banner'
                            : tagName === 'footer'
                              ? 'contentinfo'
                              : null;
          const role = roleAttr || implicitRole;

          if (role && SEMANTIC_ROLES.includes(role)) {
            const ariaLabel = current.getAttribute('aria-label');
            let name = ariaLabel;
            let css: string | null = null;
            let nameFromHeading = false;
            if (!name) {
              const heading = current.querySelector('legend,h1,h2,h3,h4,h5,h6');
              if (heading) {
                name = heading.textContent?.trim() || null;
                nameFromHeading = true;
              }
            }
            if (role === 'form') {
              const escapeAttr = (value: string): string =>
                value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
              const formName = current.getAttribute('name');
              const formId = current.getAttribute('id');
              css =
                formName && formName.trim().length > 0
                  ? `form[name="${escapeAttr(formName.trim())}"]`
                  : formId && formId.trim().length > 0
                    ? `form[id="${escapeAttr(formId.trim())}"]`
                    : 'form';
            }
            parents.push({ role, name, css, nameFromHeading });
          }
          current = current.parentElement;
        }
        return parents;
      });

      elementAncestries.push({
        index: i,
        isTarget,
        ancestors: ancestry
          .filter((a) => a.role)
          .map((a) => ({
            role: a.role!,
            name: a.name || undefined,
            css: a.css || undefined,
            nameFromHeading: a.nameFromHeading || undefined,
          })),
      });
    } catch {
      continue;
    }
  }

  const targetEl = elementAncestries.find((e) => e.isTarget);
  if (!targetEl) {
    logger.warn('[SelectorDerivation] Target not found by DOM identity in ancestry search');
    return null;
  }

  // Try each depth level to find unique parent scope
  for (let depth = 0; depth < maxParentDepth; depth++) {
    const targetAncestor = targetEl.ancestors[depth];
    if (!targetAncestor) break;

    const parentChain = targetEl.ancestors.slice(0, depth + 1);

    // Build strategy with this parent chain and verify via shared resolver
    const candidateParent: ParentContext = {
      role: parentChain[0]?.role,
      name: parentChain[0]?.name,
      css: parentChain[0]?.css,
      nameFromHeading: parentChain[0]?.nameFromHeading,
      parentChain,
    };

    const strategyWithChain = buildMinimalStrategy(selectors, selectorType, candidateParent);
    if (!strategyWithChain) continue;

    try {
      // If resolveElement returns a locator, the chain-scoped selector is unique — verified
      const resolved = await resolveElement(page, strategyWithChain, timeoutMs, { mode: 'verify' });
      if (resolved) {
        logger.debug('[SelectorDerivation] Found unique parent chain via recursive search', {
          depth,
          parentChain,
        });
        return { parentContext: candidateParent };
      }
    } catch {
      continue;
    }
  }

  // No unique parent found - fall back to matchIndex
  logger.warn('[SelectorDerivation] No unique parent found, using matchIndex fallback', {
    targetIndex: targetEl.index,
    matchCount: count,
  });
  return {
    parentContext: { matchIndex: targetEl.index },
  };
}

/**
 * Probe an element's DOM for a child SVG with a `data-icon` attribute and build
 * a CSS selector that identifies the element on the page.
 *
 * - If the selector matches exactly 1 element, returns the CSS selector as-is.
 * - If it matches 2+ elements (≤10), identifies the original element's index
 *   among matches and appends Playwright's `>> nth=N` for disambiguation.
 * - Returns undefined if the probe fails or can't disambiguate.
 */
async function probeChildIconCSS(
  page: Page,
  originalLocator: Locator,
  role: string
): Promise<string | undefined> {
  const logger = getLogger();

  const iconAttr = await originalLocator
    .evaluate((el) => {
      const svg = el.querySelector('svg[data-icon]');
      return svg?.getAttribute('data-icon') ?? null;
    })
    .catch(() => null);

  if (!iconAttr) return undefined;

  const baseSelector = `${role}:has(svg[data-icon="${iconAttr}"])`;

  try {
    const count = await page.locator(baseSelector).count();

    if (count === 1) {
      logger.debug('[SelectorDerivation] probeChildIconCSS found unique selector', {
        css: baseSelector,
      });
      return baseSelector;
    }

    if (count >= 2 && count <= 10) {
      // Multiple matches — find the original element's index among them.
      // Mark the original with a temporary attribute, then scan matches.
      const MARKER = '__probe_target__';
      await originalLocator.evaluate((el, attr) => el.setAttribute(attr, '1'), MARKER);

      try {
        const matches = page.locator(baseSelector);
        for (let i = 0; i < count; i++) {
          const hasMarker = await matches
            .nth(i)
            .evaluate((el, attr) => el.hasAttribute(attr), MARKER);
          if (hasMarker) {
            const nthSelector = `${baseSelector} >> nth=${i}`;
            logger.debug('[SelectorDerivation] probeChildIconCSS disambiguated via nth', {
              css: nthSelector,
              matchCount: count,
              index: i,
            });
            return nthSelector;
          }
        }
      } finally {
        await originalLocator
          .evaluate((el, attr) => el.removeAttribute(attr), MARKER)
          .catch(() => {});
      }
    }

    logger.debug('[SelectorDerivation] probeChildIconCSS could not disambiguate', {
      css: baseSelector,
      count,
    });
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Probe for a CSS selector based on the element's `id` attribute (or a child's `id`).
 *
 * Works for SVG shapes, chart segments, diagrams, and other elements
 * identified by `id` but lacking ARIA attributes in the original DOM.
 *
 * Disambiguation follows the same marker+nth pattern as probeChildIconCSS:
 * - If the selector matches exactly 1 element, returns the CSS selector as-is.
 * - If it matches 2–10 elements, identifies the original element's index
 *   among matches and appends Playwright's `>> nth=N` for disambiguation.
 * - Returns undefined if the probe fails or can't disambiguate.
 */
export async function probeElementIdCSS(
  page: Page,
  originalLocator: Locator
): Promise<string | undefined> {
  const logger = getLogger();

  const elementId = await originalLocator
    .evaluate((el) => {
      if (el.id) return el.id;
      const child = el.querySelector('[id]');
      return child?.id ?? null;
    })
    .catch(() => null);

  if (!elementId) return undefined;

  const baseSelector = `[id="${elementId}"]`;

  try {
    const count = await page.locator(baseSelector).count();

    if (count === 1) {
      logger.debug('[SelectorDerivation] probeElementIdCSS found unique selector', {
        css: baseSelector,
      });
      return baseSelector;
    }

    if (count >= 2 && count <= 10) {
      // Check if only 1 match is visible — return clean selector without nth
      const allMatches = page.locator(baseSelector);
      let visibleCount = 0;
      let targetIsOnlyVisible = false;

      for (let i = 0; i < count; i++) {
        const isVis = await allMatches
          .nth(i)
          .isVisible()
          .catch(() => false);
        if (isVis) {
          visibleCount++;
          if (visibleCount > 1) break; // multiple visible — skip to nth fallback
          // Check if this visible element is the original target
          const VIS_MARKER = '__vis_probe__';
          await originalLocator.evaluate((el, attr) => el.setAttribute(attr, '1'), VIS_MARKER);
          const isTarget = await allMatches
            .nth(i)
            .evaluate((el, attr) => el.hasAttribute(attr), VIS_MARKER)
            .catch(() => false);
          await originalLocator
            .evaluate((el, attr) => el.removeAttribute(attr), VIS_MARKER)
            .catch(() => {});
          if (isTarget) targetIsOnlyVisible = true;
        }
      }

      if (visibleCount === 1 && targetIsOnlyVisible) {
        logger.debug('[SelectorDerivation] probeElementIdCSS: single visible among duplicates', {
          css: baseSelector,
          matchCount: count,
        });
        return baseSelector; // Clean selector — element-resolver picks the visible one
      }

      // Fallback: disambiguate via nth index using marker
      const MARKER = '__probe_target__';
      await originalLocator.evaluate((el, attr) => el.setAttribute(attr, '1'), MARKER);

      try {
        const matches = page.locator(baseSelector);
        for (let i = 0; i < count; i++) {
          const hasMarker = await matches
            .nth(i)
            .evaluate((el, attr) => el.hasAttribute(attr), MARKER);
          if (hasMarker) {
            const nthSelector = `${baseSelector} >> nth=${i}`;
            logger.debug('[SelectorDerivation] probeElementIdCSS disambiguated via nth', {
              css: nthSelector,
              matchCount: count,
              index: i,
            });
            return nthSelector;
          }
        }
      } finally {
        await originalLocator
          .evaluate((el, attr) => el.removeAttribute(attr), MARKER)
          .catch(() => {});
      }
    }

    logger.debug('[SelectorDerivation] probeElementIdCSS could not disambiguate', {
      css: baseSelector,
      count,
    });
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Probe for a CSS selector based on the element's `title` attribute.
 *
 * Works for icon-only elements (e.g., `<a title="Quick create">`) whose
 * accessible name comes from the `title` attribute but isn't captured in
 * the a11y snapshot (Playwright renders them as empty-name elements).
 *
 * Disambiguation follows the same marker+nth pattern as probeChildIconCSS:
 * - If the selector matches exactly 1 element, returns the CSS selector as-is.
 * - If it matches 2–10 elements, identifies the original element's index
 *   among matches and appends Playwright's `>> nth=N` for disambiguation.
 * - Returns undefined if the probe fails or can't disambiguate.
 */
async function probeTitleCSS(page: Page, originalLocator: Locator): Promise<string | undefined> {
  const logger = getLogger();

  const titleAttr = await originalLocator
    .evaluate((el) => el.getAttribute('title'))
    .catch(() => null);

  if (!titleAttr) return undefined;

  const baseSelector = `[title="${titleAttr}"]`;

  try {
    const count = await page.locator(baseSelector).count();

    if (count === 1) {
      logger.debug('[SelectorDerivation] probeTitleCSS found unique selector', {
        css: baseSelector,
      });
      return baseSelector;
    }

    if (count >= 2 && count <= 10) {
      const MARKER = '__probe_target__';
      await originalLocator.evaluate((el, attr) => el.setAttribute(attr, '1'), MARKER);

      try {
        const matches = page.locator(baseSelector);
        for (let i = 0; i < count; i++) {
          const hasMarker = await matches
            .nth(i)
            .evaluate((el, attr) => el.hasAttribute(attr), MARKER);
          if (hasMarker) {
            const nthSelector = `${baseSelector} >> nth=${i}`;
            logger.debug('[SelectorDerivation] probeTitleCSS disambiguated via nth', {
              css: nthSelector,
              matchCount: count,
              index: i,
            });
            return nthSelector;
          }
        }
      } finally {
        await originalLocator
          .evaluate((el, attr) => el.removeAttribute(attr), MARKER)
          .catch(() => {});
      }
    }

    logger.debug('[SelectorDerivation] probeTitleCSS could not disambiguate', {
      css: baseSelector,
      count,
    });
    return undefined;
  } catch {
    return undefined;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Iframe Selector Resolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Probe the live DOM to resolve a generic 'iframe' frameContext into a
 * specific CSS selector (id, name, class, nth-of-type).
 *
 * Uses the element's own frame to walk up to the <iframe> host element
 * and extract identifying attributes — same approach as
 * DirectPlaywrightClient.extractElementMetadata's detectFrameContext.
 *
 * @param page - Playwright page
 * @param elementLocator - Locator for an element inside the iframe (e.g., via aria-ref)
 * @param timeoutMs - Timeout for the DOM probe (default: 5000)
 * @returns FrameContext with specific selector, or null if not in an iframe / probe fails
 */
export async function resolveSpecificIframeSelector(
  page: Page,
  elementLocator: Locator,
  timeoutMs = 5000
): Promise<FrameContext | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      elementLocator.evaluate((el) => {
        const win = el.ownerDocument.defaultView;
        if (!win || win === win.top) {
          return null; // Not in an iframe
        }

        const frameElement = win.frameElement as HTMLIFrameElement | null;
        if (!frameElement) {
          return null;
        }

        // Build the best selector for the iframe (same priority as detectFrameContext)
        const frameId = frameElement.id;
        const frameName = frameElement.name;
        const frameClass = frameElement.className;

        let selector: string;
        if (frameId) {
          selector = `iframe#${CSS.escape(frameId)}`;
        } else if (frameName) {
          selector = `iframe[name="${CSS.escape(frameName)}"]`;
        } else if (frameClass && typeof frameClass === 'string') {
          const firstClass = frameClass.split(/\s+/).filter(Boolean)[0];
          if (firstClass) {
            selector = `iframe.${CSS.escape(firstClass)}`;
          } else {
            selector = 'iframe';
          }
        } else {
          // Fallback: try to find index among direct iframe siblings
          const siblings = frameElement.parentElement
            ? Array.from(frameElement.parentElement.children).filter((c) => c.tagName === 'IFRAME')
            : [];
          if (siblings.length > 1) {
            const index = siblings.indexOf(frameElement);
            selector = `iframe:nth-of-type(${index + 1})`;
          } else {
            selector = 'iframe';
          }
        }

        return {
          type: 'iframe' as const,
          selector,
          name: frameName || undefined,
          id: frameId || undefined,
        };
      }),
      new Promise<null>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error('resolveSpecificIframeSelector timeout')),
          timeoutMs
        );
      }),
    ]);

    return result;
  } catch (err) {
    getLogger().debug('[SelectorDerivation] resolveSpecificIframeSelector failed', {
      error: err instanceof Error ? err.message : String(err),
    });
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Probe ancestor elements for `aria-rowindex`.
 *
 * Many grid-based UIs (e.g., BC Dynamics) put `aria-rowindex` on the `<tr>` parent
 * rather than on the cell/input itself. The snapshot parser only captures the element's
 * own attributes, so this probe walks up to find the nearest ancestor row index.
 *
 * @param elementLocator - Locator for the target element
 * @param timeoutMs - Timeout for the DOM probe (default: 3000)
 * @returns The aria-rowindex value, or undefined if not found
 */
export async function probeAncestorRowIndex(
  elementLocator: Locator,
  timeoutMs = 3000
): Promise<number | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      elementLocator.evaluate((el) => {
        // Check element itself first
        const own = el.getAttribute('aria-rowindex');
        if (own) return parseInt(own, 10);

        // Walk up ancestors (max 10 levels)
        let current = el.parentElement;
        let depth = 0;
        while (current && depth < 10) {
          const ri = current.getAttribute('aria-rowindex');
          if (ri) return parseInt(ri, 10);
          current = current.parentElement;
          depth++;
        }
        return undefined;
      }),
      new Promise<undefined>((_, reject) => {
        timer = setTimeout(() => reject(new Error('probeAncestorRowIndex timeout')), timeoutMs);
      }),
    ]);

    if (result !== undefined && !isNaN(result)) {
      return result;
    }
    return undefined;
  } catch (err) {
    getLogger().debug('[SelectorDerivation] probeAncestorRowIndex failed', {
      error: err instanceof Error ? err.message : String(err),
    });
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Verify a single selector type against the page using the shared element resolver.
 *
 * This ensures verification exercises the **exact same code path** as replay:
 * same serialized SelectorStrategy JSON → same resolveElement() function.
 *
 * If resolveElement returns a single-match Locator, the selector is considered
 * verified immediately — no coordinate comparison needed because the page
 * hasn't changed between derivation and verification (both happen in the BEFORE
 * phase of the same action).
 *
 * @param page - Playwright page
 * @param selectors - Full selector strategy
 * @param selectorType - Which selector to verify
 * @param originalLocator - Locator for the original element (via aria-ref)
 * @param options - Verification options
 * @returns Verification result for this selector type
 */
async function verifySingleSelector(
  page: Page,
  selectors: SelectorStrategy,
  selectorType: SelectorType,
  originalLocator: Locator,
  options: Required<SelectorVerificationOptions>
): Promise<SelectorVerificationResult> {
  const { timeoutMs } = options;
  const logger = getLogger();

  const result: SelectorVerificationResult = {
    selectorType,
    verified: false,
    matchCount: 0,
    coordinatesMatch: false,
  };

  // Build a minimal strategy with just this selector type
  const minimalStrategy = buildMinimalStrategy(selectors, selectorType);
  if (!minimalStrategy) {
    result.reason = `No ${selectorType} selector available`;
    return result;
  }

  try {
    // Dry-run the shared resolver in verify mode — same code path as replay
    const resolved = await resolveElement(page, minimalStrategy, timeoutMs, { mode: 'verify' });

    if (resolved) {
      // resolveElement returned a single-match Locator. The selector uniquely
      // resolved on the same page state, so it IS the correct element.
      result.matchCount = 1;
      result.coordinatesMatch = true;
      result.verified = true;
      return result;
    }

    // Resolution returned null — selector didn't uniquely resolve.
    // Try to discover a parent context that disambiguates.
    logger.debug('[SelectorDerivation] Initial resolution failed, trying parent disambiguation', {
      selectorType,
    });

    // Try data attribute disambiguation first (most deterministic)
    if (selectors.itemIndex !== undefined || selectors.rowIndex !== undefined) {
      // Data attributes are already included in the minimal strategy.
      // If resolveElement returned null with them, they didn't help.
      // We still record this for the result.
      logger.debug('[SelectorDerivation] Data attributes present but did not disambiguate', {
        selectorType,
        itemIndex: selectors.itemIndex,
        rowIndex: selectors.rowIndex,
      });
    }

    // Try parent context discovery (uses DOM identity for target identification)
    const parentResult = await discoverParentContext(
      page,
      selectors,
      selectorType,
      originalLocator,
      options
    );

    if (parentResult) {
      result.verified = true;
      result.coordinatesMatch = true;
      result.parentContext = parentResult.parentContext;
      return result;
    }

    result.reason = 'Resolution failed — no disambiguation strategy succeeded';
    return result;
  } catch (err) {
    result.reason = `Verification failed: ${err instanceof Error ? err.message : String(err)}`;
    return result;
  }
}

/**
 * Verify derived selectors against the actual page state.
 *
 * This function takes selectors derived from element metadata and verifies
 * them against the live page. For each selector type:
 * 1. Build a Playwright locator
 * 2. Count matching elements
 * 3. If count === 1, verify coordinates match within tolerance
 * 4. If count > 1, try to find a parent scope that makes it unique
 * 5. If count === 0, reject this selector type
 *
 * Returns only the selectors that were successfully verified, with
 * parent context added where disambiguation was needed.
 *
 * @param page - Playwright page to verify against
 * @param selectors - Selector strategy from element metadata
 * @param originalLocator - Locator for the original element (via aria-ref)
 * @param options - Verification options
 * @returns Verified selectors and detailed results
 */
export async function verifySelectorAgainstRef(
  page: Page,
  selectors: SelectorStrategy,
  originalLocator: Locator,
  options: SelectorVerificationOptions = {}
): Promise<SelectorVerificationSummary> {
  const opts: Required<SelectorVerificationOptions> = {
    ...DEFAULT_VERIFICATION_OPTIONS,
    ...options,
  };

  const results: SelectorVerificationResult[] = [];
  const verifiedSelectors: SelectorStrategy = {};

  // Selector types to verify, in priority order.
  const selectorTypes = getSelectorTypesForVerification(selectors, opts);

  if (opts.skipTextWhenStrong && selectors.text && !selectorTypes.includes('text')) {
    getLogger().debug('[SelectorDerivation] Skipping weak text verification for form control', {
      role: selectors.role?.role,
      hasTestId: !!selectors.testId,
      hasRoleName: !!selectors.role?.name,
      hasAriaLabel: !!selectors.ariaLabel,
      hasPlaceholder: !!selectors.placeholder,
    });
  }

  for (const selectorType of selectorTypes) {
    const result = await verifySingleSelector(page, selectors, selectorType, originalLocator, opts);
    results.push(result);

    if (result.verified) {
      // Add verified selector to result
      switch (selectorType) {
        case 'testId':
          if (selectors.testId) verifiedSelectors.testId = selectors.testId;
          break;
        case 'role':
          if (selectors.role) verifiedSelectors.role = { ...selectors.role };
          if (selectors.containsText) verifiedSelectors.containsText = [...selectors.containsText];
          break;
        case 'ariaLabel':
          if (selectors.ariaLabel) verifiedSelectors.ariaLabel = selectors.ariaLabel;
          break;
        case 'placeholder':
          if (selectors.placeholder) verifiedSelectors.placeholder = selectors.placeholder;
          break;
        case 'text':
          if (selectors.text) verifiedSelectors.text = { ...selectors.text };
          break;
      }

      // Add parent context if disambiguation was needed
      if (result.parentContext) {
        verifiedSelectors.parentContext = result.parentContext;
      }
    }
  }

  let anyVerified = results.some((r) => r.verified);

  // Last-resort fallback: probe for icon-only button CSS selector.
  // When all standard selectors fail (icon-only buttons have no name/text),
  // check if the element contains an SVG with a data-icon attribute and
  // build a CSS selector from it. Uses Playwright's >> nth=N syntax for
  // disambiguation when multiple elements share the same icon.
  if (!anyVerified && selectors.role) {
    const cssSelector = await probeChildIconCSS(page, originalLocator, selectors.role.role);
    if (cssSelector) {
      verifiedSelectors.css = cssSelector;
      anyVerified = true;

      getLogger().debug('[SelectorDerivation] Icon CSS probe succeeded as last resort', {
        css: cssSelector,
      });
    }
  }

  // Proactively capture CSS id selector when available, even if other selectors verified.
  // Provides defense-in-depth for cases where text/role selectors match ambiguously at replay time.
  if (anyVerified && !verifiedSelectors.css) {
    try {
      const cssSelector = await probeElementIdCSS(page, originalLocator);
      if (cssSelector) {
        verifiedSelectors.css = cssSelector;
        getLogger().debug('[SelectorDerivation] Proactive element ID CSS probe succeeded', {
          css: cssSelector,
        });
      }
    } catch {
      // Non-fatal: proactive probe is best-effort
    }
  }

  // Last-resort fallback: probe for element id CSS selector.
  // Works for SVG shapes, chart segments, diagrams, and other elements
  // identified by `id` but lacking ARIA attributes in the original DOM.
  if (!anyVerified) {
    const cssSelector = await probeElementIdCSS(page, originalLocator);
    if (cssSelector) {
      verifiedSelectors.css = cssSelector;
      anyVerified = true;

      getLogger().debug('[SelectorDerivation] Element ID CSS probe succeeded as last resort', {
        css: cssSelector,
      });
    }
  }

  // Last-resort fallback: probe for title attribute CSS selector.
  // Works for icon-only elements whose accessible name comes from the
  // `title` attribute but isn't captured in the a11y snapshot.
  if (!anyVerified) {
    const cssSelector = await probeTitleCSS(page, originalLocator);
    if (cssSelector) {
      verifiedSelectors.css = cssSelector;
      anyVerified = true;

      getLogger().debug('[SelectorDerivation] Title CSS probe succeeded as last resort', {
        css: cssSelector,
      });
    }
  }

  // Preserve original parentContext if any selector was verified with it.
  // Verification uses it via buildMinimalStrategy, but the result only
  // reports parentContext when discoverParentContext discovers a NEW one.
  if (anyVerified && selectors.parentContext && !verifiedSelectors.parentContext) {
    verifiedSelectors.parentContext = selectors.parentContext;
  }

  // Preserve frameContext in verified selectors if present
  // This ensures replay knows which frame to search in
  if (anyVerified && selectors.frameContext) {
    verifiedSelectors.frameContext = selectors.frameContext;
  }

  // Preserve childFilter for disambiguation during replay
  if (anyVerified && selectors.childFilter) {
    verifiedSelectors.childFilter = selectors.childFilter;
  }

  // Preserve data attributes for list/table disambiguation during replay
  if (anyVerified && selectors.rowIndex !== undefined) {
    verifiedSelectors.rowIndex = selectors.rowIndex;
  }
  if (anyVerified && selectors.itemIndex !== undefined) {
    verifiedSelectors.itemIndex = selectors.itemIndex;
  }
  if (anyVerified && selectors.automationId) {
    verifiedSelectors.automationId = selectors.automationId;
  }

  getLogger().debug('[SelectorDerivation] Verification complete', {
    verified: results.filter((r) => r.verified).map((r) => r.selectorType),
    rejected: results
      .filter((r) => !r.verified)
      .map((r) => ({
        type: r.selectorType,
        reason: r.reason,
      })),
    anyVerified,
    hasParentContext: !!verifiedSelectors.parentContext,
    hasFrameContext: !!selectors.frameContext,
    frameSelector: selectors.frameContext?.selector,
  });

  return {
    verifiedSelectors,
    results,
    anyVerified,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Dynamic Text Detection & Hydration
// ─────────────────────────────────────────────────────────────────────────────

/** Minimum character length for a dynamic value to be considered for templatization */
const DYNAMIC_TEXT_MIN_LENGTH = 3;

/**
 * Minimum ratio of (dynamic value length / selector text length) for a match
 * to be considered distinctive. Prevents common short words like "Open" from
 * being annotated as dynamic when they appear in longer selector text.
 * Values >= DYNAMIC_TEXT_DISTINCTIVE_LENGTH bypass the ratio check.
 */
const DYNAMIC_TEXT_MIN_RATIO = 0.4;
const DYNAMIC_TEXT_DISTINCTIVE_LENGTH = 8;

/**
 * Map of dynamic value source types and their keys.
 * Used internally by detectDynamicText.
 */
type DynamicValueEntry = {
  source: 'inputValue';
  key: string;
  value: string;
};

/**
 * Build a flat list of dynamic value entries from variable values.
 * Only includes string values above the minimum length threshold.
 * Source is always 'inputValue' — the VariableRegistry is now the single
 * source of truth and no longer distinguishes ws: prefixed keys.
 */
function buildDynamicValueEntries(variableValues: Record<string, string>): DynamicValueEntry[] {
  const entries: DynamicValueEntry[] = [];

  for (const [key, value] of Object.entries(variableValues)) {
    if (typeof value !== 'string' || value.length < DYNAMIC_TEXT_MIN_LENGTH) continue;
    entries.push({ source: 'inputValue', key, value });
  }

  // Sort by value length descending so longer matches take precedence
  entries.sort((a, b) => b.value.length - a.value.length);
  return entries;
}

/**
 * Collect all text-bearing fields from a SelectorStrategy for scanning.
 */
function collectSelectorTexts(selectors: SelectorStrategy): string[] {
  const texts: string[] = [];
  if (selectors.role?.name) texts.push(selectors.role.name);
  if (selectors.text?.text) texts.push(selectors.text.text);
  if (selectors.ariaLabel) texts.push(selectors.ariaLabel);
  if (selectors.containsText) {
    for (const t of selectors.containsText) {
      if (t) texts.push(t);
    }
  }
  return texts;
}

/**
 * Detect if a selector contains dynamic text from known dynamic values.
 *
 * Scans text-bearing selector fields (role.name, text.text, containsText[], ariaLabel)
 * against a map of known variable values from the VariableRegistry.
 *
 * @param selectors - The selector strategy to scan
 * @param variableValues - Flat key→value map from the VariableRegistry
 * @returns The dynamicText annotation if a match is found, undefined otherwise
 */
export function detectDynamicText(
  selectors: SelectorStrategy,
  variableValues: Record<string, string>
): SelectorStrategy['dynamicText'] | undefined {
  if (!variableValues || Object.keys(variableValues).length === 0) return undefined;

  const entries = buildDynamicValueEntries(variableValues);
  if (entries.length === 0) return undefined;

  const selectorTexts = collectSelectorTexts(selectors);
  if (selectorTexts.length === 0) return undefined;

  // Check each dynamic value against each selector text field.
  // To avoid false positives from common short words (e.g., "Open" matching
  // "Open settings"), we require the value to either be long enough to be
  // distinctive on its own (>= DYNAMIC_TEXT_DISTINCTIVE_LENGTH) or to cover
  // a significant portion of the selector text (>= DYNAMIC_TEXT_MIN_RATIO).
  for (const entry of entries) {
    const isDistinctiveLength = entry.value.length >= DYNAMIC_TEXT_DISTINCTIVE_LENGTH;
    for (const selectorText of selectorTexts) {
      if (!selectorText.includes(entry.value)) continue;
      if (
        isDistinctiveLength ||
        entry.value.length / selectorText.length >= DYNAMIC_TEXT_MIN_RATIO
      ) {
        return {
          source: entry.source,
          key: entry.key,
          originalText: entry.value,
        };
      }
    }
  }

  return undefined;
}

/**
 * Compute the length of the common prefix between two strings.
 */
function commonPrefixLength(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
}

/**
 * Check if a single field value is a "chimera" — compiled from mixed run data.
 *
 * Returns true when `originalText` is a strict substring of `fieldValue`,
 * AND the remaining text (field minus originalText) shares a significant
 * common prefix (≥3 chars) with `currentValue`, suggesting the leftover
 * text is a stale version of the same dynamic variable from a different run.
 */
function isChimeraFieldValue(
  fieldValue: string,
  originalText: string,
  currentValue: string
): boolean {
  if (fieldValue === originalText) return false;
  if (!fieldValue.includes(originalText)) return false;

  const remaining = fieldValue.replace(originalText, '').trim();
  if (!remaining) return false;

  const minPrefixLen = Math.min(3, currentValue.length);
  return commonPrefixLength(remaining, currentValue) >= minPrefixLen;
}

/**
 * Check if ANY text field in the selector strategy contains chimera text.
 */
function hasChimeraField(
  selectors: SelectorStrategy,
  originalText: string,
  currentValue: string
): boolean {
  if (selectors.role?.name && isChimeraFieldValue(selectors.role.name, originalText, currentValue))
    return true;
  if (selectors.text?.text && isChimeraFieldValue(selectors.text.text, originalText, currentValue))
    return true;
  if (selectors.ariaLabel && isChimeraFieldValue(selectors.ariaLabel, originalText, currentValue))
    return true;
  if (selectors.containsText?.some((t) => t && isChimeraFieldValue(t, originalText, currentValue)))
    return true;
  return false;
}

/**
 * Replace all occurrences of `originalText` with `newText` in text-bearing selector fields.
 * Returns a new SelectorStrategy (does not mutate the input).
 *
 * When `fullFieldReplacement` is true, any field that contains `originalText` as a
 * strict substring (field !== originalText) gets replaced entirely with `newText`.
 * This prevents chimera text from partial `dynamicText.originalText` values — e.g.,
 * "Matter vbEDNKOWbf- Matter ag7gOOHnZA" where only "Matter ag7gOOHnZA" was tracked
 * would produce "Matter vbEDNKOWbf- Matter wNxa4WJtqA" (stale prefix + new suffix).
 */
function replaceTextInSelectors(
  selectors: SelectorStrategy,
  originalText: string,
  newText: string,
  fullFieldReplacement = false
): SelectorStrategy {
  const result: SelectorStrategy = { ...selectors };

  // Escape special regex characters in the original text
  const escaped = originalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escaped, 'g');

  const replaceField = (fieldValue: string): string => {
    if (!fieldValue.includes(originalText)) return fieldValue;
    // If full-field replacement is on and originalText is a strict substring,
    // replace the entire field value to avoid chimera text
    if (fullFieldReplacement && fieldValue !== originalText) {
      return newText;
    }
    return fieldValue.replace(regex, newText);
  };

  if (result.testId && result.testId.includes(originalText)) {
    result.testId = replaceField(result.testId);
  }

  if (result.role?.name && result.role.name.includes(originalText)) {
    result.role = { ...result.role, name: replaceField(result.role.name) };
  }

  if (result.text?.text && result.text.text.includes(originalText)) {
    result.text = { ...result.text, text: replaceField(result.text.text) };
  }

  if (result.ariaLabel && result.ariaLabel.includes(originalText)) {
    result.ariaLabel = replaceField(result.ariaLabel);
  }

  if (result.containsText) {
    result.containsText = result.containsText.map((t) =>
      t && t.includes(originalText) ? replaceField(t) : t
    );
  }

  return result;
}

/**
 * Hydrate dynamic selectors at replay time.
 *
 * If the selector has a `dynamicText` annotation, replaces the `originalText`
 * with the current value from `variableValues[key]` in all text-bearing fields.
 * Returns a new SelectorStrategy (does not mutate the input).
 *
 * Returns the original selectors unchanged if no `dynamicText` annotation
 * is present or the key is not found in variableValues (backward compatible).
 *
 * Old cached actions with `source: 'workflowState'` still resolve because the
 * VariableRegistry seeds workflowState values under their plain key.
 *
 * @param selectors - The selector strategy to hydrate
 * @param variableValues - Flat key→value map from the VariableRegistry
 * @returns Hydrated selector strategy
 */
/**
 * Hydrate any `containsTextDynamic` bindings on parent-context nodes by
 * replacing their `containsText` with the current variable value. Returns
 * a new SelectorStrategy when anything changed, otherwise the input.
 */
function hydrateParentContextDynamicText(
  selectors: SelectorStrategy,
  variableValues: Record<string, string>
): SelectorStrategy {
  const pc = selectors.parentContext;
  if (!pc) return selectors;

  const applyBinding = (
    text: string | undefined,
    binding: ParentContextDynamicText | undefined
  ): { text: string | undefined; nextBinding: ParentContextDynamicText | undefined } => {
    if (!binding || !text) return { text, nextBinding: binding };
    const currentValue = variableValues[binding.key];
    if (!currentValue || currentValue === binding.originalText) {
      return { text, nextBinding: binding };
    }
    const hydratedText = text.includes(binding.originalText)
      ? text.split(binding.originalText).join(currentValue)
      : currentValue;
    return {
      text: hydratedText,
      nextBinding: { ...binding, originalText: currentValue },
    };
  };

  let changed = false;
  const nextPc: ParentContext = { ...pc };

  if (pc.containsTextDynamic) {
    const { text, nextBinding } = applyBinding(pc.containsText, pc.containsTextDynamic);
    if (text !== pc.containsText || nextBinding !== pc.containsTextDynamic) {
      nextPc.containsText = text;
      nextPc.containsTextDynamic = nextBinding;
      changed = true;
    }
  }

  if (pc.parentChain?.length) {
    const nextChain = pc.parentChain.map((node) => {
      if (!node.containsTextDynamic) return node;
      const { text, nextBinding } = applyBinding(node.containsText, node.containsTextDynamic);
      if (text === node.containsText && nextBinding === node.containsTextDynamic) return node;
      changed = true;
      return { ...node, containsText: text, containsTextDynamic: nextBinding };
    });
    nextPc.parentChain = nextChain;
  }

  if (!changed) return selectors;
  return { ...selectors, parentContext: nextPc };
}

export function hydrateDynamicSelectors(
  selectors: SelectorStrategy,
  variableValues: Record<string, string>
): SelectorStrategy {
  const withHydratedParent = hydrateParentContextDynamicText(selectors, variableValues);
  let hydrated = withHydratedParent;

  if (withHydratedParent.playwrightLocator) {
    hydrated = {
      ...hydrated,
      playwrightLocator: hydratePlaywrightLocatorVariables(
        withHydratedParent.playwrightLocator,
        variableValues
      ),
    };
  }
  if (withHydratedParent.playwrightLocatorCandidates) {
    hydrated = {
      ...hydrated,
      playwrightLocatorCandidates: Object.fromEntries(
        Object.entries(withHydratedParent.playwrightLocatorCandidates).map(([author, candidate]) => [
          author,
          candidate?.locator
            ? {
                ...candidate,
                locator: hydratePlaywrightLocatorVariables(candidate.locator, variableValues),
              }
            : candidate,
        ])
      ) as SelectorStrategy['playwrightLocatorCandidates'],
    };
  }

  const dynamicText = hydrated.dynamicText;
  if (!dynamicText) return hydrated;

  const { key, originalText } = dynamicText;
  selectors = hydrated;

  // Look up by plain key — VariableRegistry is the single source of truth.
  // Old cached actions with source='workflowState' still resolve because the
  // registry seeds workflowState string values under their plain key.
  const currentValue = variableValues[key];

  if (!currentValue || currentValue === originalText) {
    // No substitution needed (value unchanged or missing)
    return selectors;
  }

  // Detect chimera fields: when originalText is a strict substring of a field,
  // AND the remaining text (field minus originalText) shares a common prefix
  // with currentValue, it means the field was compiled from mixed run data.
  // E.g., field="Matter vbEDNKOWbf- Matter ag7gOOHnZA", originalText="Matter ag7gOOHnZA",
  // currentValue="Matter wNxa4WJtqA" → remaining="Matter vbEDNKOWbf-" shares prefix "Matter "
  // with currentValue → chimera detected → replace whole field.
  const hasChimera = hasChimeraField(selectors, originalText, currentValue);

  if (hasChimera) {
    getLogger().warn(
      '[SelectorDerivation] Full-field replacement triggered due to stale surrounding text',
      {
        key,
        originalText,
        currentValue,
        roleName: selectors.role?.name,
        textText: selectors.text?.text,
      }
    );
  }

  const nextSelectors = replaceTextInSelectors(selectors, originalText, currentValue, hasChimera);

  // Update the dynamicText annotation with the new originalText for tracking
  nextSelectors.dynamicText = {
    ...dynamicText,
    originalText: currentValue,
  };

  return nextSelectors;
}

function hydratePlaywrightLocatorVariables(
  locator: string,
  variableValues: Record<string, string>
): string {
  return locator.replace(/\{\{var\.([a-zA-Z0-9_]+)\}\}/g, (match, key: string) => {
    const value = variableValues[key];
    if (value === undefined) return match;
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  });
}
